/**
 * Color-Blind Mode — React context + localStorage persistence.
 *
 * Provides a universally-safe KPI color palette as an alternative to the
 * standard red/yellow/green/grey scheme.  Based on the Wong (2011) palette
 * and IBM Design for Color Blindness guidelines.
 *
 * L4E status semantics:
 *   blue   = Complete
 *   green  = In Progress / OK
 *   yellow = Warning
 *   red    = Critical
 *   grey   = No data yet
 *   notApplicable = Not applicable (dark grey)
 *
 * The safe palette replaces:
 *   red    → vermillion         #D55E00  (distinct from teal for deuteranopes)
 *   green  → bluish teal        #009E73  (distinct from both red AND blue)
 *   yellow → amber/gold         #E69F00  (high contrast, universally safe)
 *   grey   → grey               #999999  (unchanged, neutral)
 *   completed → blue            #0072B2  (kept blue — semantic match)
 *   notApplicable → dark grey   #404040  (unchanged, dark neutral)
 *
 * Usage in components:
 *   import { useColorBlindMode } from '../theme/colorBlindMode';
 *   const { enabled, toggle, resolveIndicatorColor } = useColorBlindMode();
 *
 * Usage in non-component code (utils, helpers):
 *   import { isColorBlindModeEnabled, getActiveIndicatorPalette } from '../theme/colorBlindMode';
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

/* ── Palette definitions ─────────────────────────────────────────── */

export interface IndicatorPalette {
  red: string;
  yellow: string;
  green: string;
  grey: string;
  completed: string;
  notApplicable: string;
}

export const STANDARD_PALETTE: IndicatorPalette = {
  red:            '#D53541',   // Critical
  yellow:         '#ffd13a',   // Warning
  green:          '#45BB47',   // In Progress / OK
  grey:           '#9CA3AF',   // No data yet
  completed:      '#106DD5',   // Complete (blue)
  notApplicable:  '#4B5563',   // Not applicable (dark grey)
};

export const COLOR_BLIND_SAFE_PALETTE: IndicatorPalette = {
  red:            '#D55E00',   // Vermillion — distinct from teal for deuteranopes
  yellow:         '#E69F00',   // Amber/gold — high contrast, universally safe
  green:          '#009E73',   // Bluish teal — distinct from both red AND blue
  grey:           '#999999',   // Neutral grey — no data
  completed:      '#0072B2',   // Blue — semantic match for "complete"
  notApplicable:  '#404040',   // Dark grey — not applicable
};

/* ── localStorage key ────────────────────────────────────────────── */

const LS_KEY = 'l4e_colorBlindMode';

/** Read the persisted preference (non-hook, safe for utility code). */
export function isColorBlindModeEnabled(): boolean {
  try {
    return localStorage.getItem(LS_KEY) === '1';
  } catch {
    return false;
  }
}

/** Get the currently active palette (non-hook, safe for utility code). */
export function getActiveIndicatorPalette(): IndicatorPalette {
  return isColorBlindModeEnabled() ? COLOR_BLIND_SAFE_PALETTE : STANDARD_PALETTE;
}

/* ── React context ───────────────────────────────────────────────── */

interface ColorBlindModeContextValue {
  enabled: boolean;
  toggle: () => void;
  setEnabled: (v: boolean) => void;
  palette: IndicatorPalette;
}

const ColorBlindModeContext = createContext<ColorBlindModeContextValue>({
  enabled: false,
  toggle: () => {},
  setEnabled: () => {},
  palette: STANDARD_PALETTE,
});

export function ColorBlindModeProvider({ children }: { children: React.ReactNode }) {
  const [enabled, setEnabledState] = useState<boolean>(isColorBlindModeEnabled);

  const setEnabled = useCallback((v: boolean) => {
    setEnabledState(v);
    try { localStorage.setItem(LS_KEY, v ? '1' : '0'); } catch { /* noop */ }
  }, []);

  const toggle = useCallback(() => {
    setEnabled(!enabled);
  }, [enabled, setEnabled]);

  const palette = useMemo(
    () => (enabled ? COLOR_BLIND_SAFE_PALETTE : STANDARD_PALETTE),
    [enabled],
  );

  const value = useMemo(
    () => ({ enabled, toggle, setEnabled, palette }),
    [enabled, toggle, setEnabled, palette],
  );

  return (
    <ColorBlindModeContext.Provider value={value}>
      {children}
    </ColorBlindModeContext.Provider>
  );
}

/** Hook for components that need to read or toggle color-blind mode. */
export function useColorBlindMode() {
  return useContext(ColorBlindModeContext);
}
